import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../store";
import { Plus, Trash2, Edit2, Check, X } from "lucide-react";

const EMPTY = { label: "Home", name: "", phone: "", address_line1: "", address_line2: "",
  city: "", state: "", postal_code: "", country: "India", is_default: false };

export default function Addresses() {
  const { API, user, authHeaders } = useApp();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/addresses`, { headers: authHeaders }).then(r => setItems(r.data));
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) {
    return <div className="pt-40 text-center text-[#8A8D8A] font-serif-display text-2xl">Please sign in to manage addresses.</div>;
  }

  const save = async () => {
    setBusy(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/addresses/${editing.id}`, editing, { headers: authHeaders });
      } else {
        await axios.post(`${API}/addresses`, editing, { headers: authHeaders });
      }
      setEditing(null); load();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await axios.delete(`${API}/addresses/${id}`, { headers: authHeaders });
    load();
  };

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-[1200px] mx-auto" data-testid="addresses-page">
      <div className="overline text-copper mb-4">Saved Addresses</div>
      <h1 className="headline-xl text-[clamp(3rem,6vw,6rem)] mb-12">Address Book<span className="italic text-[#8A8D8A]">.</span></h1>

      {!editing && (
        <>
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary mb-10" data-testid="add-address">
            <Plus size={14} /> Add new address
          </button>

          {items.length === 0 ? (
            <p className="text-[#8A8D8A] italic font-serif-display text-xl">No addresses saved yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-1">
              {items.map(a => (
                <div key={a.id} className="bg-sand p-6 relative" data-testid={`addr-${a.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="overline text-[10px] text-copper">{a.label}</div>
                      {a.is_default && <span className="text-[10px] uppercase tracking-widest bg-[#1E1F1E] text-[#FAF9F6] px-2 py-0.5">Default</span>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setEditing({ ...a })} className="text-copper" data-testid={`edit-addr-${a.id}`}><Edit2 size={14} /></button>
                      <button onClick={() => remove(a.id)} className="text-[#9E3C3C]" data-testid={`remove-addr-${a.id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="font-serif-display text-xl mb-1">{a.name}</div>
                  <div className="text-sm text-[#5C5E5C] leading-relaxed">
                    {a.address_line1}{a.address_line2 ? <>, {a.address_line2}</> : null}<br/>
                    {a.city}, {a.state} {a.postal_code}<br/>
                    {a.country}<br/>
                    <span className="text-xs text-[#8A8D8A]">{a.phone}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <div className="max-w-2xl">
          <h2 className="font-serif-display text-3xl mb-8">{editing.id ? "Edit address" : "New address"}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <F label="Label (Home / Office...)" v={editing.label} on={v => setEditing({ ...editing, label: v })} testid="addr-label" />
            <F label="Recipient name" v={editing.name} on={v => setEditing({ ...editing, name: v })} testid="addr-name" />
            <F label="Phone" v={editing.phone} on={v => setEditing({ ...editing, phone: v })} testid="addr-phone" />
            <div></div>
            <div className="md:col-span-2"><F label="Address line 1" v={editing.address_line1} on={v => setEditing({ ...editing, address_line1: v })} testid="addr-line1" /></div>
            <div className="md:col-span-2"><F label="Address line 2" v={editing.address_line2} on={v => setEditing({ ...editing, address_line2: v })} testid="addr-line2" /></div>
            <F label="City" v={editing.city} on={v => setEditing({ ...editing, city: v })} testid="addr-city" />
            <F label="State" v={editing.state} on={v => setEditing({ ...editing, state: v })} testid="addr-state" />
            <F label="Postal code" v={editing.postal_code} on={v => setEditing({ ...editing, postal_code: v })} testid="addr-zip" />
            <F label="Country" v={editing.country} on={v => setEditing({ ...editing, country: v })} testid="addr-country" />
          </div>
          <label className="flex items-center gap-2 mt-6 text-sm cursor-pointer">
            <input type="checkbox" checked={!!editing.is_default} onChange={e => setEditing({ ...editing, is_default: e.target.checked })} data-testid="addr-default" />
            Make this my default address
          </label>
          <div className="flex gap-3 mt-8">
            <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-addr">
              {busy ? "Saving…" : "Save address"}
            </button>
            <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, v, on, testid }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-2">{label}</label>
      <input value={v || ""} onChange={e => on(e.target.value)} className="input-underline" data-testid={testid} />
    </div>
  );
}
